import type { PreOrderFlow as OfflinePreOrderFlow } from "@stockline/shared/types/orders/pre-order-flow";
import type { PreOrderFlow as LegacyPreOrderFlow } from "@/app/api/types/preOrder";

/**
 * Rename `@stockline/web`'s camelCase offline flow row into the legacy
 * snake_case `PreOrderFlow` shape the recap cells consume. Drops the
 * enriched `product` / `unit` / `partner` objects (cells resolve those
 * themselves from the recap-wide context caches), so the result is purely a
 * field-rename pass with no side-loaded lookups.
 */
export function mapOfflineFlowToLegacy(
  f: OfflinePreOrderFlow,
): LegacyPreOrderFlow {
  return {
    id: f.id,
    pre_order_id: f.preOrderId ?? "",
    product_id: f.productId,
    partner_id: f.partnerId,
    quantity: f.quantity,
    weight: f.weight,
    unit_id: f.unitId,
    price_per_weight: f.pricePerWeight,
    comment: f.comment,
    hidden_comment: f.hiddenComment,
    status: f.status,
    index: f.flowIndex,
    delivery_date: f.deliveryDate,
    order_date: f.orderDate,
    flow_metadata: f.flowMetadata,
    created_at: f.createdAt,
    updated_at: f.updatedAt,
    deleted_at: f.deletedAt,
  };
}

/**
 * Sort offline flows the same way the legacy recap pipeline did:
 * by `flowIndex` ascending (nulls last), then by `createdAt` ascending as
 * the tie-breaker. Returns a new array; input is not mutated.
 */
export function sortOfflineFlows<T extends OfflinePreOrderFlow>(
  flows: T[],
): T[] {
  return [...flows].sort((a, b) => {
    const ai = a.flowIndex ?? Number.MAX_SAFE_INTEGER;
    const bi = b.flowIndex ?? Number.MAX_SAFE_INTEGER;
    if (ai !== bi) return ai - bi;
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });
}
